import React, { useEffect, useRef } from "react";
import { ChevronRight, LayoutGrid, Boxes, ShieldCheck, Monitor, PackagePlus, Layers, CornerDownRight, ExternalLink, Crosshair, Info } from "lucide-react";
import { SYMBOLS, parseCell, formatPrice, LEGEND } from "../../lib/matrix";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";

const SECTION_ICONS = {
  office: Layers,
  ems: ShieldCheck,
  windows: Monitor,
  suite: Boxes,
  related: PackagePlus,
};

const highlight = (text, query) => {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark>{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
};

const CellContent = ({ raw }) => {
  const cell = parseCell(raw);
  switch (cell.kind) {
    case "check":
      return <span className="sym sym-check">{SYMBOLS.check}</span>;
    case "addon":
      return <span className="sym sym-addon" title="Add-on available">{SYMBOLS.addon}</span>;
    case "included":
      return <span className="sym sym-inc" title="Included in the add-on above">{SYMBOLS.included}</span>;
    case "separate":
      return <span className="sym sym-sep" title="Available for separate purchase">{SYMBOLS.separate}</span>;
    case "text":
      return (
        <span className="cell-text">
          {cell.text.map((line, i) => (
            <React.Fragment key={i}>
              {i > 0 && <br />}
              {line}
            </React.Fragment>
          ))}
        </span>
      );
    default:
      return null;
  }
};

const PlanHeader = ({ plan, priceMode, showPrices, pricing, groupStart }) => {
  const symbol = pricing?.symbol || "$";
  const price = plan.price ? plan.price[priceMode] : null;
  const prev = plan.prev ? plan.prev[priceMode] : null;
  const changed = prev !== null && price !== null && price !== prev;
  const isUS = !pricing?.region || pricing.region === "us";
  const label = price === null && isUS ? plan.priceLabel : "";
  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>
        <th className={`plan-th ${groupStart ? "group-start" : ""}`} scope="col" data-testid={`plan-header-${plan.id}`}>
          <span className="plan-short">{plan.short}</span>
          {showPrices && price !== null && (
            <span className={`plan-price ${changed ? "changed" : ""} ${plan.estimated ? "estimated" : ""}`}>
              {plan.addon ? "+" : ""}
              {formatPrice(price, symbol, plan.estimated)}
            </span>
          )}
          {showPrices && price === null && label && <span className="plan-price label">{label}</span>}
        </th>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="text-left max-w-xs">
        <div className="font-semibold">{plan.name}</div>
        {plan.price ? (
          <>
            {plan.addon ? (
              <div>Add-on: {formatPrice(plan.price.teams, symbol, plan.estimated)} / user / month on top of the base suite</div>
            ) : (
              <>
                <div>With Teams: {formatPrice(plan.price.teams, symbol, plan.estimated)} / user / month</div>
                <div>No Teams: {formatPrice(plan.price.noTeams, symbol, plan.estimated)} / user / month</div>
              </>
            )}
            {changed && (
              <div className="opacity-80">
                Previously {formatPrice(prev, symbol)} ({pricing?.previousLabel})
              </div>
            )}
            {plan.estimated && <div className="opacity-80">{"\u2248"} Estimated: converted from the US list price with Microsoft's regional ratio - no official local price published.</div>}
            <div className="opacity-80">
              {pricing?.currency || "USD"}, annual subscription. Effective {pricing?.effective}.
            </div>
          </>
        ) : (
          <div className="opacity-80">{label ? `${label}. ` : ""}{isUS ? "" : `No ${pricing?.currency} list price published for this plan.`}</div>
        )}
        {plan.priceNote && <div className="opacity-80 mt-1">{plan.priceNote}</div>}
      </TooltipContent>
    </Tooltip>
  );
};

export default function MatrixGrid({
  rows,
  groups,
  plans,
  visiblePlans,
  pricing,
  search,
  priceMode,
  showPrices,
  timeline,
  featureMap,
  onToggleMapCell,
  onMapNameChange,
  totalFeatures,
  shownFeatures,
  version,
  onLookup,
}) {
  const scrollRef = useRef(null);
  const theadRef = useRef(null);
  const hasMap = !!featureMap;
  const visibleCount = visiblePlans.length;

  // Keep section rows sticky right below the (variable height) header.
  useEffect(() => {
    const thead = theadRef.current;
    const scroller = scrollRef.current;
    if (!thead || !scroller) return undefined;
    const apply = () => scroller.style.setProperty("--thead-h", `${thead.getBoundingClientRect().height}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(thead);
    return () => ro.disconnect();
  }, [showPrices, visibleCount, timeline, hasMap]);

  const planCols = plans.filter((p) => visiblePlans.includes(p.id));
  const groupStarts = new Set();
  let lastGroup = null;
  planCols.forEach((p) => {
    if (p.group !== lastGroup) groupStarts.add(p.id);
    lastGroup = p.group;
  });
  const extraCols = (timeline ? 1 : 0) + (featureMap ? 1 : 0);
  const totalCols = 1 + extraCols + planCols.length;

  return (
    <TooltipProvider>
      <div className="grid-scroll" ref={scrollRef} data-testid="grid-scroll">
        <table className="mx-table" id="main" data-testid="matrix-table">
          <thead ref={theadRef}>
            <tr className="head-groups">
              <th scope="col" />
              {timeline && <th className="extra-col" />}
              {featureMap && <th className="extra-col" />}
              {groups.map((group) => {
                const count = planCols.filter((p) => p.group === group.id).length;
                if (!count) return null;
                return (
                  <th key={group.id} colSpan={count} scope="colgroup" className="group-start" data-testid={`group-header-${group.id}`}>
                    {group.tableLabel}
                  </th>
                );
              })}
            </tr>
            <tr className="head-plans">
              <th scope="col">Feature</th>
              {timeline && (
                <th className="extra-col" scope="col">
                  Changed
                </th>
              )}
              {featureMap && (
                <th className="extra-col map-th" scope="col">
                  <input
                    value={featureMap.name}
                    onChange={(e) => onMapNameChange(e.target.value)}
                    aria-label="Feature map name"
                    data-testid="map-name-input"
                  />
                </th>
              )}
              {planCols.map((plan) => (
                <PlanHeader
                  key={plan.id}
                  plan={plan}
                  priceMode={priceMode}
                  showPrices={showPrices}
                  pricing={pricing}
                  groupStart={groupStarts.has(plan.id)}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={totalCols} className="empty-state" data-testid="empty-state">
                  No features match your search or filters.
                </td>
              </tr>
            )}
            {rows.map((row) => {
              if (row.type === "section") {
                const Icon = SECTION_ICONS[row.cls] || Layers;
                const hasChips = Object.keys(row.header || {}).length > 0;
                return (
                  <tr key={row.id} className={`section-row sec-${row.cls}`} data-testid={`section-${row.sectionKey}`}>
                    <th scope="row">
                      <Tooltip delayDuration={150}>
                        <TooltipTrigger asChild>
                          <span className="sec-title" tabIndex={0}>
                            <Icon size={14} aria-hidden="true" />
                            {row.title}
                            {hasChips && <Info size={12} className="sec-info" aria-hidden="true" />}
                          </span>
                        </TooltipTrigger>
                        {hasChips && (
                          <TooltipContent side="bottom" className="text-left max-w-xs">
                            The chips in this row show which {row.title} plan each license includes - e.g. Microsoft 365 E7 bundles {row.title} E5 (there is no{" "}
                            {row.title} E7).
                          </TooltipContent>
                        )}
                      </Tooltip>
                    </th>
                    {timeline && <th />}
                    {featureMap && <th />}
                    {planCols.map((plan) => (
                      <th key={plan.id} className={groupStarts.has(plan.id) ? "group-start" : ""}>
                        {row.header?.[plan.id] ? (
                          <Tooltip delayDuration={150}>
                            <TooltipTrigger asChild>
                              <span className="sec-chip" tabIndex={0} data-testid={`section-chip-${row.sectionKey}-${plan.id}`}>
                                {row.header[plan.id]}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              {plan.name} includes {row.title} {row.header[plan.id]}
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          ""
                        )}
                      </th>
                    ))}
                  </tr>
                );
              }
              if (row.type === "sub") {
                return (
                  <tr key={row.id} className="sub-row" data-testid={`sub-${row.id}`}>
                    <th scope="row">
                      <span className="sub-title">
                        <CornerDownRight size={12} aria-hidden="true" />
                        {row.title}
                      </span>
                    </th>
                    {Array.from({ length: totalCols - 1 }).map((_, i) => (
                      <th key={i} />
                    ))}
                  </tr>
                );
              }
              const mapOn = featureMap ? !!featureMap.values[row.id] : false;
              const indent = Math.min(row.indent || 0, 5);
              return (
                <tr key={row.id} className="feature-row" data-testid="feature-row">
                  <td className={`feature-cell indent${indent}`}>
                    <span className="feature-inner">
                      <span className={`feature-name ${row.group ? "group" : ""}`}>
                        {indent > 0 && <ChevronRight size={12} className="grip" aria-hidden="true" />}
                        {row.url ? (
                          <a href={row.url} target="_blank" rel="noopener noreferrer">
                            {highlight(row.name, search)}
                          </a>
                        ) : (
                          <span>{highlight(row.name, search)}</span>
                        )}
                      </span>
                      {row.diagram ? (
                        <span className="row-actions">
                          <button
                            type="button"
                            className="diagram-icon"
                            onClick={() => onLookup && onLookup(row.name)}
                            title="Which licenses include this?"
                            aria-label={`Find licenses for ${row.name}`}
                            data-testid={`lookup-row-${row.id}`}
                          >
                            <Crosshair size={13} aria-hidden="true" />
                          </button>
                          <a
                            className="diagram-icon"
                            href={row.diagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={`${row.name} diagram`}
                            aria-label={`${row.name} diagram`}
                          >
                            <LayoutGrid size={13} aria-hidden="true" />
                          </a>
                        </span>
                      ) : (
                        <span className="row-actions">
                          <button
                            type="button"
                            className="diagram-icon"
                            onClick={() => onLookup && onLookup(row.name)}
                            title="Which licenses include this?"
                            aria-label={`Find licenses for ${row.name}`}
                            data-testid={`lookup-row-${row.id}`}
                          >
                            <Crosshair size={13} aria-hidden="true" />
                          </button>
                          {row.url && (
                            <a
                              className="diagram-icon"
                              href={row.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Open documentation"
                              aria-label={`${row.name} documentation`}
                            >
                              <ExternalLink size={12} aria-hidden="true" />
                            </a>
                          )}
                        </span>
                      )}
                    </span>
                  </td>
                  {timeline && (
                    <td className={row.date ? "filled" : ""}>{row.date && <span className="cell-date">{row.date}</span>}</td>
                  )}
                  {featureMap && (
                    <td
                      className={`map-cell ${mapOn ? "on filled" : ""}`}
                      onClick={() => onToggleMapCell(row.id)}
                      role="checkbox"
                      aria-checked={mapOn}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onToggleMapCell(row.id);
                        }
                      }}
                      data-testid={`map-cell-${row.id}`}
                    >
                      {mapOn && <span className="sym sym-check">{SYMBOLS.check}</span>}
                    </td>
                  )}
                  {planCols.map((plan) => {
                    const raw = row.cells?.[plan.id] || "";
                    const note = row.notes?.[plan.id];
                    return (
                      <td
                        key={plan.id}
                        className={`${raw ? "filled" : ""} ${groupStarts.has(plan.id) ? "group-start" : ""}`}
                        title={note || undefined}
                      >
                        <CellContent raw={raw} />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="grid-footer" data-testid="legend-footer">
        <div className="legend">
          {LEGEND.slice(0, 4).map((item) => (
            <span key={item.label} title={item.label}>
              <b
                className={
                  item.symbol === SYMBOLS.check
                    ? "sym sym-check"
                    : item.symbol === SYMBOLS.addon
                      ? "sym sym-addon"
                      : item.symbol === SYMBOLS.separate
                        ? "sym sym-sep"
                        : "sym sym-inc"
                }
              >
                {item.symbol}
              </b>
              {item.label.split(" (")[0]}
            </span>
          ))}
        </div>
        <div className="spacer" />
        <span data-testid="row-counter">
          Showing <strong style={{ color: "var(--text-2)" }}>{shownFeatures}</strong> of {totalFeatures} features · {visibleCount} licenses
        </span>
        {pricing?.source && (
          <a href={pricing.source} target="_blank" rel="noopener noreferrer">
            Pricing source
          </a>
        )}
        {version && <span>v{version}</span>}
      </div>
    </TooltipProvider>
  );
}
